import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { makeCredentialsProvider } from './cognito'
import { getIdentityId } from '../upload/s3'
import { isNativeGoogleSignInAvailable } from './nativeAuth'
import { silentGoogleReauth } from './silentReauth'
import { AuthContext } from './AuthContext'
import { refreshDelayMs } from './refreshSchedule'
import type { AuthContextValue, AuthState, GoogleUser } from './types'

const SESSION_KEY = 'google_id_token'

// Silent-renewal failures are retried (transient Cognito 5xx, network blips,
// FedCM hiccups) before falling back to a logout at the token's real expiry.
const MAX_RENEWAL_ATTEMPTS = 3
const RENEWAL_RETRY_DELAY_MS = 60_000

// Decode a JWT's payload segment (base64url, UTF-8 safe for non-ASCII names).
// Returns null for a malformed token rather than throwing so callers can decide
// whether a missing/invalid payload is fatal.
function decodeJwtPayloadRaw(token: string): Record<string, unknown> | null {
  const [, payload] = token.split('.')
  if (!payload) return null
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '==='.slice((normalized.length + 3) % 4)
  try {
    const binary = atob(padded)
    const json = decodeURIComponent(
      Array.from(binary)
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    )
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return null
  }
}

function decodeJwtPayload(token: string): GoogleUser {
  const decoded = decodeJwtPayloadRaw(token)
  if (!decoded) throw new Error('Malformed Google ID token')
  return {
    sub: decoded.sub as string,
    email: decoded.email as string,
    name: decoded.name as string | undefined,
    picture: decoded.picture as string | undefined,
  }
}

function getExpFromToken(token: string): number | null {
  const exp = decodeJwtPayloadRaw(token)?.exp
  return typeof exp === 'number' ? exp : null
}

function getCachedToken(): string | null {
  try {
    const token = sessionStorage.getItem(SESSION_KEY)
    if (!token) return null
    const exp = getExpFromToken(token)
    if (exp && exp > Date.now() / 1000 + 60) return token
    sessionStorage.removeItem(SESSION_KEY)
    return null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  // Validate a Google ID token with Cognito and swap it into the session.
  // Throws on failure WITHOUT touching existing state, so the silent-renewal
  // path can fail transiently (Cognito 5xx, network blip) while the current
  // still-valid session stays intact.
  const establishSession = useCallback(async (idToken: string) => {
    const user = decodeJwtPayload(idToken)
    const provider = makeCredentialsProvider(idToken)
    // Resolve once to validate the login and capture the Cognito identityId;
    // then store the provider (not the resolved creds) so the S3 client can
    // refresh the temporary credentials as they expire.
    const identityId = getIdentityId(await provider())
    sessionStorage.setItem(SESSION_KEY, idToken)
    setState({ status: 'authenticated', user, idToken, identityId, credentials: provider })
  }, [])

  // Interactive login: on failure there is no session worth keeping, so tear
  // down and surface the error to the login screen.
  const login = useCallback(
    async (idToken: string) => {
      try {
        await establishSession(idToken)
      } catch (err) {
        sessionStorage.removeItem(SESSION_KEY)
        const message = err instanceof Error ? err.message : 'Login failed'
        setState({ status: 'unauthenticated', error: message })
        throw err
      }
    },
    [establishSession],
  )

  useEffect(() => {
    let cancelled = false
    const cached = getCachedToken()
    if (!cached) {
      setState({ status: 'unauthenticated' })
      return
    }
    login(cached).catch(() => {
      if (!cancelled) setState({ status: 'unauthenticated' })
    })
    return () => {
      cancelled = true
    }
  }, [login])

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY)
    window.google?.accounts.id.disableAutoSelect()
    setState({ status: 'unauthenticated' })
  }, [])

  const idToken = state.status === 'authenticated' ? state.idToken : null

  // Silently renew the Google ID token shortly before it expires so the derived
  // AWS credentials keep refreshing and the session survives past ~1h without a
  // visible re-login. On the native iOS shell the token comes from the native
  // bridge, which must expose its own silent refresh (separate repo) — skip here
  // and let that path fall back to re-login. A successful renewal replaces the
  // idToken in state, which re-keys this effect: the new run schedules the next
  // renewal and the old run's cleanup cancels its stale continuations. Failures
  // never tear the session down early — we retry a few times, then let the
  // session run to the token's real expiry and only log out then.
  // Keyed on idToken (not the whole state) so unrelated state changes don't
  // re-arm the timer.
  useEffect(() => {
    if (!idToken) return
    if (isNativeGoogleSignInAvailable()) return
    const payload = decodeJwtPayloadRaw(idToken)
    const exp = typeof payload?.exp === 'number' ? payload.exp : null
    const sub = typeof payload?.sub === 'string' ? payload.sub : null
    if (!exp) return
    const expiresAtMs = exp * 1000

    // Guards every async continuation: after logout or a successful renewal
    // (both change idToken and run this cleanup), in-flight work must not
    // resurrect a signed-out session or clobber a newer one.
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    let attempts = 0

    const scheduleExpiryLogout = () => {
      timer = setTimeout(
        () => {
          if (cancelled) return
          console.warn('[auth] Google token expired without renewal; signing out')
          logout()
        },
        Math.max(0, expiresAtMs - Date.now()),
      )
    }

    const retryOrExpire = (err: unknown) => {
      const remaining = expiresAtMs - Date.now()
      if (attempts < MAX_RENEWAL_ATTEMPTS && remaining > RENEWAL_RETRY_DELAY_MS + 60_000) {
        console.warn('[auth] silent Google re-auth attempt failed; will retry', err)
        timer = setTimeout(attempt, RENEWAL_RETRY_DELAY_MS)
      } else {
        console.warn(
          '[auth] silent Google re-auth failed; keeping session until token expiry',
          err,
        )
        scheduleExpiryLogout()
      }
    }

    const attempt = async () => {
      attempts += 1
      let newToken: string
      try {
        newToken = await silentGoogleReauth()
      } catch (err) {
        if (!cancelled) retryOrExpire(err)
        return
      }
      if (cancelled) return
      // A multi-account browser can hand back a *different* previously-approved
      // account (login hints are advisory). Never silently switch identities
      // mid-session — and don't retry either, since prompting again would just
      // re-offer the same wrong account.
      if (sub && decodeJwtPayloadRaw(newToken)?.sub !== sub) {
        console.warn(
          '[auth] silent Google re-auth returned a different account; keeping session until token expiry',
        )
        scheduleExpiryLogout()
        return
      }
      try {
        await establishSession(newToken)
        console.log('[auth] silent Google re-auth succeeded; session renewed')
      } catch (err) {
        // Transient Cognito/network failure — current session is untouched.
        if (!cancelled) retryOrExpire(err)
      }
    }

    timer = setTimeout(() => void attempt(), refreshDelayMs(expiresAtMs))
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [idToken, establishSession, logout])

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, logout }),
    [state, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
